import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

type Status = 'online' | 'offline' | 'unknown';

interface DaemonState {
  status: Status;
  queued: number;
  last_seen?: string | null;
}

interface Props {
  live?: { online: boolean; queued: number };
}

/**
 * DaemonStatus per docs/DESIGN-DECISIONS.md §1.2.
 *
 * Phone: a Prism-state icon. Tap reveals a tooltip card with queued count
 * and last-seen. The icon stays subtle at rest (the brand recedes).
 *
 * Tablet+: the same Prism plus an inline mono label "online · 0 queued"
 * (rendered by the parent on the tablet/desktop top bar).
 *
 * Live updates from /ws/console override polling.
 */
export function DaemonStatus({ live }: Props) {
  const [state, setState] = useState<DaemonState>({ status: 'unknown', queued: 0 });
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let mounted = true;
    async function refresh() {
      try {
        const r = await api.daemonStatus();
        if (!mounted) return;
        setState({
          status: r.online ? 'online' : 'offline',
          queued: r.queued,
          last_seen: r.last_seen,
        });
      } catch {
        if (!mounted) return;
        setState((prev) => ({ ...prev, status: 'unknown' }));
      }
    }
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (!live) return;
    setState((prev) => ({
      ...prev,
      status: live.online ? 'online' : 'offline',
      queued: live.queued,
    }));
  }, [live]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (popRef.current?.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const statusLabel =
    state.status === 'online' ? 'online' : state.status === 'offline' ? 'offline' : 'unknown';

  return (
    // flex (not absolute) so the inline tablet+ label takes real layout
    // width — earlier the label was absolutely positioned and overlapped
    // adjacent TopBar buttons (the + and History icons) once they landed
    // alongside it.
    <div className="flex items-center gap-1">
      <span
        className="hidden md:inline-block font-mono text-[10px] uppercase
                   tracking-mono-wide text-paper-400 whitespace-nowrap"
      >
        {state.status} · {state.queued} queued
      </span>
      <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Daemon ${statusLabel}, ${state.queued} queued`}
        onClick={() => setOpen((v) => !v)}
        className="w-11 h-11 flex items-center justify-center rounded-md
                   hover:bg-night-800 focus:outline-none focus:bg-night-800"
      >
        <DaemonGlyph status={state.status} />
      </button>

      {open && (
        <div
          ref={popRef}
          role="dialog"
          className="absolute right-0 top-full mt-1 z-30 w-56
                     bg-night-800 border border-night-600 rounded-md shadow-lg p-3"
        >
          <div className="font-mono text-[10px] uppercase tracking-mono-wide text-paper-400">
            daemon
          </div>
          <div className="mt-1 text-sm text-paper-100">
            {state.status === 'online' && 'connected'}
            {state.status === 'offline' && 'not connected'}
            {state.status === 'unknown' && 'state not yet known'}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="text-paper-400 font-mono uppercase tracking-mono">queued</div>
            <div className="text-paper-100 font-mono">{state.queued}</div>
            {state.last_seen && (
              <>
                <div className="text-paper-400 font-mono uppercase tracking-mono">last</div>
                <div className="text-paper-100 font-mono truncate" title={state.last_seen}>
                  {formatLastSeen(state.last_seen)}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function formatLastSeen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/**
 * The DaemonStatus glyph: a small Prism shape whose facets shift by state.
 * The shape is the same Prism used elsewhere; the colors are state-coded
 * via the palette: pine for online (the trail is open), wheat for offline
 * (the gathering has not completed), neutral gray for unknown.
 */
function DaemonGlyph({ status }: { status: Status }) {
  const palette =
    status === 'online'
      ? { hue: 150, chroma: 1.0 }   // pine
      : status === 'offline'
      ? { hue: 85, chroma: 0.45 }   // wheat, muted
      : { hue: 0, chroma: 0 };      // neutral gray

  const c = (base: number) => (base * palette.chroma).toFixed(3);
  const mid = palette.chroma === 0 ? 'oklch(56% 0 0)' : `oklch(66% ${c(0.13)} ${palette.hue})`;
  const dark = palette.chroma === 0 ? 'oklch(40% 0 0)' : `oklch(48% ${c(0.09)} ${palette.hue})`;
  const edge = palette.chroma === 0 ? 'oklch(70% 0 0 / 0.55)' : `oklch(86% ${c(0.1)} ${palette.hue} / 0.55)`;

  const size = 16;
  const pad = 1.5;
  const apex = `${size / 2},${pad}`;
  const baseLeft = `${pad},${size - pad}`;
  const baseRight = `${size - pad},${size - pad}`;
  const midBottom = `${size / 2},${size - pad}`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <polygon points={`${apex} ${baseRight} ${baseLeft}`} fill={mid} />
      <polygon points={`${apex} ${midBottom} ${baseLeft}`} fill={dark} />
      <line
        x1={size / 2}
        y1={pad}
        x2={size - pad}
        y2={size - pad}
        stroke={edge}
        strokeWidth="0.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
